import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useState, FormEvent, useEffect, useRef } from 'react'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

export default function FenceEstimate() {
  const [fenceFeet, setFenceFeet] = useState('')
  const [fenceMaterial, setFenceMaterial] = useState('')

  const [hasOldFenceRemoval, setHasOldFenceRemoval] = useState('No')

  const [terrain, setTerrain] = useState('Flat / Easy Access')

  const [hasPrivacySlats, setHasPrivacySlats] = useState('No')

  const [hasGate, setHasGate] = useState('No')
  const [gateCount, setGateCount] = useState('')

  const [hasRVGate, setHasRVGate] = useState('No')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')

  const [estimatedPrice, setEstimatedPrice] = useState('$0.00')
  const [monthlyPayment, setMonthlyPayment] = useState('$0.00')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)

  // REAL pricing — carried over from existing deck calculator (fenceMaterialPrices)
  // CONFIRMED: PT 4x4 / PT 4x6 / Cedar 4x4 / Cedar 4x6
  // UNCONFIRMED (flagged): Chain-Link, Vinyl/PVC, Composite — pulled from blog cost guide
  // as $/linear ft ranges, midpoint used. Get real numbers before launch.
  const fenceMaterialPrices: Record<string, number> = {
    'Chain-Link': 32.5,                    // UNCONFIRMED — blog range $25-40/ft
    'Pressure Treated 4x4 Posts': 68,      // CONFIRMED — from deck calculator
    'Pressure Treated 4x6 Posts': 72,      // CONFIRMED — from deck calculator
    'Cedar 4x4 Posts': 80,                 // CONFIRMED — from deck calculator
    'Cedar 4x6 Posts': 84,                 // CONFIRMED — from deck calculator
    'Vinyl / PVC': 62.5,                   // UNCONFIRMED — blog range $55-70/ft
    'Composite': 115,                      // UNCONFIRMED — blog range $90-140/ft
  }

  // UNCONFIRMED: no terrain modifier exists in the current deck calculator's pricing model.
  // These multipliers apply only to the labor share (assumed 45% of total, per blog).
  const terrainMultipliers: Record<string, number> = {
    'Flat / Easy Access': 1.0,
    'Sloped / Uneven Grade': 1.15,
    'Heavy Clay / Rock': 1.25,
    'Narrow Lot / No Bobcat Access': 1.35,
  }

  const LABOR_SHARE = 0.45 // UNCONFIRMED — blog states 40-50% of total cost
  const OLD_FENCE_REMOVAL_RATE = 10 // UNCONFIRMED — blog range $5-15/ft, midpoint used
  const PRIVACY_SLAT_RATE = 10      // UNCONFIRMED — blog range $5-15/ft, midpoint used
  const GATE_COST = 625             // CONFIRMED — from deck calculator
  const RV_GATE_COST = 950          // CONFIRMED — from deck calculator

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const formData = {
        firstName,
        lastName,
        phone,
        email,
        fenceFeet,
        fenceMaterial,
        hasOldFenceRemoval,
        terrain,
        hasPrivacySlats,
        hasGate,
        gateCount,
        hasRVGate,
        estimatedPrice,
        monthlyPayment
      };

      const response = await fetch('https://formspree.io/f/xqewgkwz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        toast.success("Estimate submitted successfully!");
        
        // Redirect to the Thank You page
        window.history.pushState({}, '', '/thank-you?form=fence_calculator');
        window.dispatchEvent(new Event('popstate'));
        window.scrollTo(0, 0);
      } else {
        setSubmitError('Failed to submit estimate. Please try again later.');
        toast.error("Failed to submit estimate. Please try again later.");
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
      toast.error("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    updateEstimate()
  }, [
    fenceFeet, fenceMaterial,
    hasOldFenceRemoval,
    terrain,
    hasPrivacySlats,
    hasGate, gateCount,
    hasRVGate
  ])

  const updateEstimate = () => {
    const lft = parseFloat(fenceFeet) || 0
    const materialRate = fenceMaterial ? (fenceMaterialPrices[fenceMaterial] || 0) : 0
    const isChainLink = fenceMaterial === 'Chain-Link'

    const materialCost = lft * materialRate
    const laborCost = materialCost * LABOR_SHARE * (terrainMultipliers[terrain] || 1)

    const removalCost = hasOldFenceRemoval === 'Yes' ? lft * OLD_FENCE_REMOVAL_RATE : 0
    const slatCost = (isChainLink && hasPrivacySlats === 'Yes') ? lft * PRIVACY_SLAT_RATE : 0

    const gates = hasGate === 'Yes' ? parseInt(gateCount) || 0 : 0
    const gateCost = gates * GATE_COST

    const rvGateCost = hasRVGate === 'Yes' ? RV_GATE_COST : 0

    const totalCost = materialCost + laborCost + removalCost + slatCost + gateCost + rvGateCost

    setEstimatedPrice(`$${totalCost.toFixed(2)}`)
    setMonthlyPayment(`$${(totalCost * 0.01351).toFixed(2)}`)
  }

  const selectClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const inputClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const labelClass = "block text-sm font-medium text-gray-700 mb-1"

  // Consolidated Schema Data for the Fence Estimate Page
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "Busy Beaver Fence Building Calculator",
        "operatingSystem": "Web",
        "applicationCategory": "ConstructionBusinessApplication",
        "offers": { 
          "@type": "Offer", 
          "price": "0", 
          "priceCurrency": "CAD" 
        },
        "description": "Calculate custom fence installation costs in Calgary instantly using current local pricing for chain-link, wood, cedar, vinyl, and composite fencing."
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Fence Estimate",
            "item": "https://busybeaverconstruction.com/fence-estimate"
          }
        ]
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>Fence Cost Calculator Calgary | Online Fence Estimator</title>
        <meta name="description" content="Calculate your custom fence installation cost in Calgary instantly. Get pricing for chain-link, pressure-treated wood, cedar, vinyl, and composite fencing with our free online tool." />
        <link rel="canonical" href="https://busybeaverconstruction.com/fence-estimate" />
      </Helmet>
      
      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      
      <Header />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />

      <section className="relative h-[400px] flex items-center justify-center">
        <img
          src="/assets/images/2020/04/Decoartive-picket-top-Vinyl-Fence-Chestnut-Brown.jpg"
          alt="Custom fence installation by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover object-bottom loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Calgary's Leading Custom Deck & Fence Builder</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="container">
          <h1 className="text-center text-5xl lg:text-6xl font-primary mb-6">Calculate Your Fence - Online</h1>

          <div className="mb-10 p-8 bg-gray-50 border-l-8 border-beaver-green max-w-4xl mx-auto shadow-sm">
            <p className="text-lg md:text-xl text-text-dark font-text leading-relaxed">
              Our <strong>fence installation calculator</strong> provides instant cost estimates for Calgary homeowners, covering <strong>chain-link</strong>, <strong>pressure-treated wood</strong>, <strong>cedar</strong>, <strong>vinyl</strong>, and <strong>composite</strong> fencing.
            </p>
          </div>

          <div className="text-center mb-8">
            <p className="text-gray-700 font-text">
              Contact us now to schedule a free onsite estimate. <a href="tel:4032865400" className="text-beaver-green font-bold hover:underline">403-286-5400</a>
            </p>
          </div>

          <div className="max-w-4xl mx-auto border border-gray-300 mb-10">
            <div className="bg-white p-6">
              <h2 className="text-xl font-bold text-red-600 mb-4 uppercase">Calculate The Cost of Your Fence!</h2>

              <form onSubmit={handleSubmit} ref={formRef}>
                <div className="mb-4">
                  <label className={labelClass}>Fence Linear Feet:</label>
                  <div className="flex">
                    <input
                      type="number"
                      value={fenceFeet}
                      onChange={(e) => setFenceFeet(e.target.value)}
                      className={`${inputClass} mr-2`}
                      placeholder="Value"
                      required
                    />
                    <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                      LFT
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Fence Material:</label>
                  <select
                    value={fenceMaterial}
                    onChange={(e) => setFenceMaterial(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="">Select Material</option>
                    <option value="Chain-Link">Chain-Link</option>
                    <option value="Pressure Treated 4x4 Posts">Pressure Treated 4x4 Posts</option>
                    <option value="Pressure Treated 4x6 Posts">Pressure Treated 4x6 Posts</option>
                    <option value="Cedar 4x4 Posts">Cedar 4x4 Posts</option>
                    <option value="Cedar 4x6 Posts">Cedar 4x6 Posts</option>
                    <option value="Vinyl / PVC">Vinyl / PVC</option>
                    <option value="Composite">Composite</option>
                  </select>
                </div>

                {fenceMaterial === 'Chain-Link' && (
                  <div className="mb-4">
                    <label className={labelClass}>Privacy Slats:</label>
                    <select
                      value={hasPrivacySlats}
                      onChange={(e) => setHasPrivacySlats(e.target.value)}
                      className={selectClass}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                )}

                <div className="mb-4">
                  <label className={labelClass}>Site Terrain / Access:</label>
                  <select
                    value={terrain}
                    onChange={(e) => setTerrain(e.target.value)}
                    className={selectClass}
                  >
                    <option value="Flat / Easy Access">Flat / Easy Access</option>
                    <option value="Sloped / Uneven Grade">Sloped / Uneven Grade</option>
                    <option value="Heavy Clay / Rock">Heavy Clay / Rock</option>
                    <option value="Narrow Lot / No Bobcat Access">Narrow Lot / No Bobcat Access</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Old Fence Removal Needed:</label>
                  <select
                    value={hasOldFenceRemoval}
                    onChange={(e) => setHasOldFenceRemoval(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Gate:</label>
                  <select
                    value={hasGate}
                    onChange={(e) => setHasGate(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {hasGate === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Number of Gates:</label>
                    <select
                      value={gateCount}
                      onChange={(e) => setGateCount(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select # of Gates</option>
                      {[...Array(6)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-4">
                  <label className={labelClass}>RV Gate:</label>
                  <select
                    value={hasRVGate}
                    onChange={(e) => setHasRVGate(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Estimated Price:</label>
                  <div className="py-2 px-3 bg-yellow-100 border border-yellow-200 font-bold text-lg">
                    {estimatedPrice}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price provided does not include GST and is only an estimate. An in-person quotation is still required and may result in prices different than provided.
                  </p>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Your low monthly payment:</label>
                  <div className="py-2 px-3 bg-beaver-red/10 border border-beaver-red/20 font-bold text-lg text-beaver-red">
                    {monthlyPayment}
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-red-600 text-white p-4">
              <h3 className="text-xl font-bold mb-1 uppercase font-secondary">Claim This Estimate</h3>
              <p className="text-sm mb-4">Book Your Site Visit - Get A Professional Quote</p>

              {submitSuccess ? (
                <div className="bg-green-600 text-white p-4 rounded shadow-inner">
                  <p className="font-bold">Thank you for your submission!</p>
                  <p>We have sent you a copy of your estimate to your email. A representative will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} ref={formRef}>
                  {submitError && (
                    <div className="bg-yellow-600 text-white p-3 mb-4 rounded font-bold">
                      <p>{submitError}</p>
                    </div>
                  )}

                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="firstName"
                    />
                  </div>

                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="lastName"
                    />
                  </div>

                  <div className="mb-3">
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="phone"
                    />
                  </div>

                  <div className="mb-3">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="email"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-black text-white font-bold py-2 px-4 hover:brightness-125 transition-all disabled:bg-gray-500 uppercase tracking-widest"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Estimate Request'}
                  </button>
                </form>
              )}
            </div>
          </div>

          
        </div>
      </section>

      <AssociationsAndSuppliers />
      <Footer />
    </main>
  )
}